/**
 * Benutzerverwaltung - Olympus Betreuungsverwaltung
 * Admin-Bereich für Benutzerverwaltung
 */

import React, { useState, useEffect } from 'react';
import {
  Users,
  Plus,
  Edit,
  Trash2,
  Shield,
  Briefcase,
  UserCheck,
  RefreshCw,
  Search,
  MoreVertical,
  Check,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
// Dialog imports removed - all forms are now inline
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { authAPI } from '@/api/authAPI';
import { useAuth } from '@/contexts/AuthContext';

const ROLLEN = [
  { value: 'admin', label: 'Administrator', icon: Shield, color: 'bg-red-500' },
  { value: 'betreuer', label: 'Betreuer', icon: Briefcase, color: 'bg-blue-500' },
  { value: 'sachbearbeiter', label: 'Sachbearbeiter', icon: UserCheck, color: 'bg-green-500' },
];

export default function BenutzerPage() {
  const { user: currentUser, refreshUser } = useAuth();
  const [benutzer, setBenutzer] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterRolle, setFilterRolle] = useState('alle');
  const [dialog, setDialog] = useState({ open: false, mode: 'create', data: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, benutzer: null });

  const loadBenutzer = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterRolle !== 'alle') params.rolle = filterRolle;
      const data = await authAPI.listBenutzer(params);
      setBenutzer(data);
    } catch (error) {
      toast.error('Fehler beim Laden: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBenutzer();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterRolle]);

  const filteredBenutzer = benutzer.filter(b => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      b.vorname?.toLowerCase().includes(searchLower) ||
      b.nachname?.toLowerCase().includes(searchLower) ||
      b.email?.toLowerCase().includes(searchLower)
    );
  });

  const handleSave = async (formData) => {
    try {
      if (dialog.mode === 'create') {
        await authAPI.createBenutzer(formData);
        toast.success('Benutzer erstellt');
      } else {
        await authAPI.updateBenutzer(dialog.data.id, formData);
        toast.success('Benutzer aktualisiert');
        // Eigenen User aktualisiert? Auth-Context refreshen
        if (dialog.data.id === currentUser?.id) {
          await refreshUser();
        }
      }
      setDialog({ open: false, mode: 'create', data: null });
      loadBenutzer();
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  const handleDelete = async () => {
    try {
      await authAPI.deleteBenutzer(deleteDialog.benutzer.id);
      toast.success('Benutzer gelöscht');
      setDeleteDialog({ open: false, benutzer: null });
      loadBenutzer();
    } catch (error) {
      toast.error('Fehler: ' + error.message);
    }
  };

  const getRolleInfo = (rolle) => ROLLEN.find(r => r.value === rolle) || ROLLEN[2];

  return (
    <div className="space-y-6" data-testid="benutzer-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Benutzerverwaltung</h1>
          <p className="text-slate-500">{benutzer.length} Benutzer im System</p>
        </div>
        <Button onClick={() => setDialog({ open: true, mode: 'create', data: null })} data-testid="new-user-btn">
          <Plus className="w-4 h-4 mr-2" />
          Neuer Benutzer
        </Button>
      </div>

      {/* Filter */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Suchen nach Name oder E-Mail..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="user-search-input"
                />
              </div>
            </div>
            <Select value={filterRolle} onValueChange={setFilterRolle}>
              <SelectTrigger className="w-full sm:w-48" data-testid="role-filter">
                <SelectValue placeholder="Rolle filtern" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="alle">Alle Rollen</SelectItem>
                {ROLLEN.map(r => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadBenutzer}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Benutzer Liste */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : filteredBenutzer.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">Keine Benutzer gefunden</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredBenutzer.map((b) => {
                const rolleInfo = getRolleInfo(b.rolle);
                const RolleIcon = rolleInfo.icon;
                const isCurrentUser = b.id === currentUser?.id;

                return (
                  <div
                    key={b.id}
                    className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                      isCurrentUser ? 'border-blue-200 bg-blue-50' : 'border-slate-200 hover:bg-slate-50'
                    }`}
                    data-testid={`user-row-${b.id}`}
                  >
                    <div className="flex items-center gap-4">
                      {/* Avatar */}
                      <div className={`w-10 h-10 rounded-full ${rolleInfo.color} flex items-center justify-center text-white font-medium`}>
                        {b.vorname?.[0]}{b.nachname?.[0]}
                      </div>

                      {/* Info */}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-700">
                            {b.vorname} {b.nachname}
                          </span>
                          {isCurrentUser && (
                            <Badge variant="outline" className="text-xs">Sie</Badge>
                          )}
                          {!b.aktiv && (
                            <Badge variant="destructive" className="text-xs">Deaktiviert</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500">{b.email}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      {/* Rolle Badge */}
                      <Badge variant="secondary" className="hidden sm:flex items-center gap-1">
                        <RolleIcon className="w-3 h-3" />
                        {rolleInfo.label}
                      </Badge>

                      {/* Aktionen */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setDialog({ open: true, mode: 'edit', data: b })}>
                            <Edit className="w-4 h-4 mr-2" />
                            Bearbeiten
                          </DropdownMenuItem>
                          {!isCurrentUser && (
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => setDeleteDialog({ open: true, benutzer: b })}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Löschen
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Benutzer Dialog */}
      <BenutzerDialog
        open={dialog.open}
        mode={dialog.mode}
        data={dialog.data}
        onClose={() => setDialog({ open: false, mode: 'create', data: null })}
        onSave={handleSave}
      />

      {/* Inline Lösch-Bestätigung */}
      {deleteDialog.open && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-red-50 border border-red-300 rounded-xl shadow-lg p-4 flex items-center gap-4 max-w-lg" data-testid="inline-delete-confirm">
          <p className="text-sm text-red-800">
            Benutzer <strong>{deleteDialog.benutzer?.vorname} {deleteDialog.benutzer?.nachname}</strong> wirklich löschen?
          </p>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={() => setDeleteDialog({ open: false, benutzer: null })}>Abbrechen</Button>
            <Button variant="destructive" size="sm" onClick={handleDelete} data-testid="confirm-delete-btn">Löschen</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Benutzer Dialog Komponente
function BenutzerDialog({ open, mode, data, onClose, onSave }) {
  const [formData, setFormData] = useState({
    vorname: '',
    nachname: '',
    email: '',
    rolle: 'sachbearbeiter',
    passwort: '',
    aktiv: true,
    telefon: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && data) {
      setFormData({
        vorname: data.vorname || '',
        nachname: data.nachname || '',
        email: data.email || '',
        rolle: data.rolle || 'sachbearbeiter',
        passwort: '',
        aktiv: data.aktiv !== false,
        telefon: data.telefon || ''
      });
    } else {
      setFormData({
        vorname: '',
        nachname: '',
        email: '',
        rolle: 'sachbearbeiter',
        passwort: '',
        aktiv: true,
        telefon: ''
      });
    }
  }, [mode, data, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const submitData = { ...formData };
    if (mode === 'edit' && !submitData.passwort) {
      delete submitData.passwort;
    }
    
    try {
      await onSave(submitData);
    } finally {
      setLoading(false);
    }
  };

  return (
    open ? (
    <Card className="max-w-md mx-auto mt-4 border-slate-300 shadow-lg" data-testid="benutzer-form-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{mode === 'create' ? 'Neuer Benutzer' : 'Benutzer bearbeiten'}</CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        <CardDescription>{mode === 'create' ? 'Erstellen Sie einen neuen Benutzer' : 'Bearbeiten Sie die Benutzerdaten'}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="vorname">Vorname</Label>
              <Input id="vorname" value={formData.vorname} onChange={(e) => setFormData({ ...formData, vorname: e.target.value })} required data-testid="user-firstname-input" />
            </div>
            <div>
              <Label htmlFor="nachname">Nachname</Label>
              <Input id="nachname" value={formData.nachname} onChange={(e) => setFormData({ ...formData, nachname: e.target.value })} required data-testid="user-lastname-input" />
            </div>
          </div>
          <div>
            <Label htmlFor="email">E-Mail</Label>
            <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="user-email-input" />
          </div>
          <div>
            <Label htmlFor="rolle">Rolle</Label>
            <Select value={formData.rolle} onValueChange={(v) => setFormData({ ...formData, rolle: v })}>
              <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLLEN.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="passwort">{mode === 'create' ? 'Passwort' : 'Neues Passwort (leer lassen = unverändert)'}</Label>
            <Input id="passwort" type="password" value={formData.passwort} onChange={(e) => setFormData({ ...formData, passwort: e.target.value })} required={mode === 'create'} minLength={6} placeholder={mode === 'edit' ? '------' : ''} data-testid="user-password-input" />
          </div>
          <div>
            <Label htmlFor="telefon">Telefon (optional)</Label>
            <Input id="telefon" value={formData.telefon} onChange={(e) => setFormData({ ...formData, telefon: e.target.value })} />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="aktiv" checked={formData.aktiv} onChange={(e) => setFormData({ ...formData, aktiv: e.target.checked })} className="rounded" />
            <Label htmlFor="aktiv" className="cursor-pointer">Benutzer ist aktiv</Label>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>Abbrechen</Button>
            <Button type="submit" disabled={loading} data-testid="save-user-btn">{loading ? 'Speichern...' : 'Speichern'}</Button>
          </div>
        </form>
      </CardContent>
    </Card>
    ) : null
  );
}
